/**
 * DashboardCharts Component
 *
 * Displays quote distribution and timeline charts
 */

import { QuoteStatus } from '@/types';
import { Typography } from 'antd';
import React from 'react';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

const { Title, Text } = Typography;

// Chart colors matching Victorian Sliders branding
const CHART_COLORS: Record<QuoteStatus, string> = {
  [QuoteStatus.Pending]: '#22D3EE',
  [QuoteStatus.Quoted]: '#3B82F6',
  [QuoteStatus.Sent]: '#94A3B8',
  [QuoteStatus.Ordered]: '#10B981',
  [QuoteStatus.PaymentReceived]: '#059669',
  [QuoteStatus.Completed]: '#8B5CF6',
  [QuoteStatus.FollowedUp]: '#F59E0B',
  [QuoteStatus.Issue]: '#EF4444',
};

interface DashboardChartsProps {
  distributionData?: any[];
  timelineData?: any[];
  distributionLoading?: boolean;
  timelineLoading?: boolean;
}

export const DashboardCharts: React.FC<DashboardChartsProps> = ({
  distributionData,
  timelineData,
  distributionLoading: _distributionLoading = false,
  timelineLoading: _timelineLoading = false,
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Quote Status Distribution - Donut Chart */}
      <div className="bg-white rounded-lg p-4 shadow-none">
        <Title level={5} className="!mb-1">
          Quote Status Distribution
        </Title>
        <Text type="secondary" className="text-sm">
          Breakdown of quotes by current status
        </Text>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={distributionData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
              >
              {distributionData?.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[entry.status as QuoteStatus]} />
                ))}
              </Pie>
              <RechartsTooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>

      </div>

      {/* Quotes Over Time - Line Chart */}
     <div className="bg-white rounded-lg p-4 shadow-none">
        <Title level={5} className="!mb-1">
          Quotes Over Time
        </Title>
        <Text type="secondary" className="text-sm">
          Number of quotes created in the last 30 days
        </Text>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <RechartsTooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#EF4444"
                strokeWidth={2}
                dot={{ fill: '#EF4444' }}
                name="Quotes"
              />
            </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DashboardCharts;
